'use client';

import { useRef, useState } from 'react';
import type { z } from 'zod';

import type { FieldIssue } from '@/app/lib/forms/tracking/types';
import type {
  FieldRefs,
  FormState,
  FormValues,
  UseFormOptions,
} from '@/app/lib/forms/types/form.types';
import { scrollIntoViewIfNeeded } from '@/app/utils/scrollIntoViewIfNeed';

const FOCUSABLE_SELECTOR =
  'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Custom hook for managing form state, validation, and submission with Zod schema validation.
 * @param schema - Zod schema for form validation
 * @param initialValues - Initial form values
 * @param validators - Custom field validators
 * @returns Form state, validation, and submission functions
 *
 * Read more: [Storybook docs: `useForm`](https://tis-web-story-book.netlify.app/?path=/docs/hooks-useform--documentation)
 */
export const useForm = <T extends z.ZodType>({
  schema,
  getSchemaForParse,
  initialValues = {} as FormValues<T>,
  validators = {},
  tracking,
}: UseFormOptions<T>) => {
  const fieldRefs = useRef<FieldRefs<FormValues<T>>>({});
  const initialValuesRef = useRef(initialValues);
  const [values, setValues] = useState<FormValues<T>>(
    initialValues as FormValues<T>
  );
  // Latest values for tracking, which runs outside the state updaters.
  const valuesRef = useRef<FormValues<T>>(values);
  const blurListeners = useRef<
    Partial<Record<keyof FormValues<T>, { el: HTMLElement; fn: () => void }>>
  >({});
  const [state, setState] = useState<
    FormState<FormValues<T>> & { isSubmitted: boolean }
  >({
    errors: {},
    isSubmitting: false,
    isSubmitted: false,
  });

  const resolveParseSchema = (vals: FormValues<T>) => {
    if (getSchemaForParse) {
      return getSchemaForParse(vals) as T;
    }
    return schema;
  };

  const validateForm = (vals: FormValues<T>) => {
    const result = resolveParseSchema(vals).safeParse(vals);
    const emptyErrors: Partial<Record<keyof FormValues<T>, string>> = {};
    if (result.success) {
      return { isValid: true, errors: emptyErrors, issues: {} };
    }
    const errors: Partial<Record<keyof FormValues<T>, string>> = {};
    // Same first issue per field, with its code: tracking reports the error type.
    const issues: Partial<Record<string, FieldIssue>> = {};
    for (const issue of result.error.issues) {
      const field = issue.path[0] as keyof FormValues<T> | undefined;
      if (field && !errors[field]) {
        errors[field] = issue.message;
        issues[field as string] = { message: issue.message, code: issue.code };
      }
    }
    return { isValid: false, errors, issues };
  };

  const scrollToFirstError = (
    errors: Partial<Record<keyof FormValues<T>, string>>
  ) => {
    const element = (
      Object.keys(errors) as (keyof FormValues<T>)[]
    ).reduce<HTMLElement | null>((first, field) => {
      const el = fieldRefs.current[field];
      if (!el) return first;
      if (!first) return el;
      return first.compareDocumentPosition(el) &
        Node.DOCUMENT_POSITION_PRECEDING
        ? el
        : first;
    }, null);

    if (!element) return;

    const isElementFocusable =
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLSelectElement ||
      element instanceof HTMLButtonElement ||
      element.tabIndex >= 0;

    const focusTarget = isElementFocusable
      ? element
      : (element.querySelector(FOCUSABLE_SELECTOR) as HTMLElement | null);

    focusTarget?.focus();
    scrollIntoViewIfNeeded(element, null, { block: 'center' });
  };

  const validate = () => {
    const { isValid, errors, issues } = validateForm(values);
    tracking?.onSubmit({ isValid, issues, values });
    setState((prev) => ({ ...prev, isSubmitted: true, errors }));
    if (!isValid) scrollToFirstError(errors);
    return isValid;
  };

  const handleSubmit = (
    onValid: (data: FormValues<T>) => void | Promise<void>
  ) => {
    return async (e?: React.FormEvent) => {
      if (e && e.cancelable) e.preventDefault();

      const { isValid, errors, issues } = validateForm(values);
      tracking?.onSubmit({ isValid, issues, values });

      setState((prev) => ({
        ...prev,
        isSubmitting: isValid,
        isSubmitted: true,
        errors: errors,
      }));

      if (!isValid) {
        scrollToFirstError(errors);
        return;
      }

      try {
        await onValid(values);
      } finally {
        setState((prev) => ({
          ...prev,
          isSubmitting: false,
        }));
      }
    };
  };

  /** `track: false` for values the page fills in (e.g. from the URL), not the customer. */
  const setValue = <K extends keyof FormValues<T>>(
    field: K,
    value: FormValues<T>[K],
    { track = true }: { track?: boolean } = {}
  ) => {
    const validator = validators[field as string];
    let transformedValue = value;

    if (validator?.transform) {
      transformedValue = validator.transform(value);
    }

    if (!track) {
      valuesRef.current = { ...valuesRef.current, [field]: transformedValue };
    } else if (
      // Skip writes that leave the value as it was, e.g. a form clearing an
      // already-empty dependent field: those are not the customer's input.
      tracking?.enabled &&
      !Object.is(valuesRef.current[field], transformedValue)
    ) {
      const nextValues = { ...valuesRef.current, [field]: transformedValue };
      valuesRef.current = nextValues;
      tracking.onFieldChange(
        field as string,
        transformedValue,
        validateForm(nextValues).issues[field as string]
      );
    }

    setValues((prevValues) => {
      const newValues = { ...prevValues, [field]: transformedValue };

      // Check for custom onChange validator first
      if (typeof validator?.onChange === 'function') {
        const error = validator.onChange(transformedValue, newValues);
        setState((prev) => {
          const newErrors = { ...prev.errors };
          if (error) newErrors[field] = error;
          else delete newErrors[field];
          return { ...prev, errors: newErrors };
        });
      } else {
        // If no custom validator, only validate after form has been submitted
        setState((prev) => {
          if (!prev.isSubmitted) {
            // Before submission, don't validate
            return prev;
          }

          // After submission, validate on change
          const { errors: schemaErrors } = validateForm(newValues);
          const fieldError = schemaErrors[field];
          const newErrors = { ...prev.errors };

          if (fieldError) {
            newErrors[field] = fieldError;
          } else {
            delete newErrors[field];
          }

          return { ...prev, errors: newErrors };
        });
      }

      return newValues;
    });
  };

  const setError = <K extends keyof FormValues<T>>(
    field: K,
    message: string
  ) => {
    setState((prev) => ({
      ...prev,
      errors: { ...prev.errors, [field]: message },
    }));
  };

  const clearErrors = <K extends keyof FormValues<T>>(field?: K) => {
    setState((prev) => {
      if (field) {
        const newErrors = { ...prev.errors };
        delete newErrors[field];
        return { ...prev, errors: newErrors };
      }
      return { ...prev, errors: {} };
    });
  };

  const reset = () => {
    valuesRef.current = initialValuesRef.current as FormValues<T>;
    setValues(initialValuesRef.current as FormValues<T>);
    setState({
      errors: {},
      isSubmitting: false,
      isSubmitted: false,
    });
  };

  // Blur tracking rides on the registered element; `focusout` bubbles, so a
  // wrapper element works as well as the input itself.
  const trackBlur = <K extends keyof FormValues<T>>(
    field: K,
    el: HTMLElement | null
  ) => {
    const current = blurListeners.current[field];
    if (current?.el === el) return;
    current?.el.removeEventListener('focusout', current.fn);
    delete blurListeners.current[field];
    if (!el || !tracking?.enabled) return;

    const fn = () => {
      const latest = valuesRef.current;
      tracking.onFieldBlur(
        field as string,
        latest[field],
        validateForm(latest).issues[field as string]
      );
    };
    el.addEventListener('focusout', fn);
    blurListeners.current[field] = { el, fn };
  };

  const registerRef = <K extends keyof FormValues<T>>(field: K) => {
    return (el: HTMLElement | null) => {
      trackBlur(field, el);
      if (el) {
        fieldRefs.current[field] = el;
        return;
      }
      delete fieldRefs.current[field];
    };
  };

  return {
    validate,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    reset,
    registerRef,
    values,
    formState: state,
    fieldRefs,
  };
};
